import type { FC, ReactNode } from 'react'
import { createContext, useContext, useMemo, useState } from 'react'

import type {
  Address,
  BuyoutType,
  EcomPolicyMandatoryProduct,
  EcomPolicyStep,
  Product,
  GrossSalaryTaxBracket,
} from '@/components/ecom-policy/types'

type PolicyContextValue = {
  activeStep: [EcomPolicyStep, (v: EcomPolicyStep) => void]
  selectedProductIds: [number[], (v: number[]) => void]
  selectedAccessoriesIds: [number[], (v: number[]) => void]
  mandatoryProducts: [EcomPolicyMandatoryProduct[], (v: EcomPolicyMandatoryProduct[]) => void]
  selectedGreenpoolProductId: [number | null, (v: number | null) => void]
  isPrivateDeliveryAddress: [boolean, (v: boolean) => void]
  useCustomAddress: [boolean, (v: boolean) => void]
  address: [Partial<Address> | null, (v: Partial<Address> | null) => void]
  isTermsAccepted: [boolean, (v: boolean) => void]
  assetBuyoutType: [Record<number, BuyoutType>, (v: Record<number, BuyoutType>) => void]
  orderTotalCost: [number, (v: number) => void]
  transportationProduct: [Product | null, (v: Product | null) => void]
  productQuantityById: [Record<number, number>, (v: Record<number, number>) => void]
  changedProductPrices: [
    Record<number, { oldPrice: number; newPrice: number }>,
    (v: Record<number, { oldPrice: number; newPrice: number }>) => void,
  ]
  grossSalaryTaxBracket: [GrossSalaryTaxBracket | null, (v: GrossSalaryTaxBracket | null) => void]
  coverInterval: [number, (v: number) => void]
  grossSalaryBaseTax: [number, (v: number) => void]
  grossSalaryTopTax: [number, (v: number) => void]
}

const PolicyContext = createContext<PolicyContextValue | undefined>(undefined)

const usePolicyProviderValue = (): PolicyContextValue => {
  const activeStep = useState<EcomPolicyStep>('GREENPOOL_PRODUCT_SELECT')
  const selectedProductIds = useState<number[]>([])
  const mandatoryProducts = useState<EcomPolicyMandatoryProduct[]>([])
  const selectedAccessoriesIds = useState<number[]>([])
  const selectedGreenpoolProductId = useState<number | null>(null)
  const isPrivateDeliveryAddress = useState<boolean>(false)
  const useCustomAddress = useState<boolean>(false)
  const address = useState<Partial<Address> | null>(null)
  const isTermsAccepted = useState<boolean>(false)
  const assetBuyoutType = useState<Record<number, BuyoutType>>({})
  const orderTotalCost = useState<number>(0)
  const transportationProduct = useState<Product | null>(null)
  const productQuantityById = useState<Record<number, number>>({})
  const changedProductPrices = useState<Record<number, { oldPrice: number; newPrice: number }>>({})
  const grossSalaryTaxBracket = useState<GrossSalaryTaxBracket | null>(null)
  const coverInterval = useState<number>(0)
  const grossSalaryBaseTax = useState<number>(0)
  const grossSalaryTopTax = useState<number>(0)

  return useMemo(
    () => ({
      activeStep,
      selectedProductIds,
      selectedAccessoriesIds,
      mandatoryProducts,
      selectedGreenpoolProductId,
      isPrivateDeliveryAddress,
      useCustomAddress,
      address,
      isTermsAccepted,
      assetBuyoutType,
      orderTotalCost,
      transportationProduct,
      productQuantityById,
      changedProductPrices,
      grossSalaryTaxBracket,
      coverInterval,
      grossSalaryBaseTax,
      grossSalaryTopTax,
    }),
    [
      activeStep,
      selectedProductIds,
      selectedAccessoriesIds,
      mandatoryProducts,
      selectedGreenpoolProductId,
      isPrivateDeliveryAddress,
      useCustomAddress,
      address,
      isTermsAccepted,
      assetBuyoutType,
      orderTotalCost,
      transportationProduct,
      productQuantityById,
      changedProductPrices,
      grossSalaryTaxBracket,
      coverInterval,
      grossSalaryBaseTax,
      grossSalaryTopTax,
    ]
  )
}

export const PolicyContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const value = usePolicyProviderValue()
  return <PolicyContext.Provider value={value}>{children}</PolicyContext.Provider>
}

export const usePolicyContext = () => {
  const ctx = useContext(PolicyContext)
  if (!ctx) {
    throw new Error('usePolicyContext must be used within a PolicyContextProvider')
  }
  return ctx
}
